import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { chantiersApi, CATEGORY_LABEL } from '../services/chantiersApi';
import { elusApi, TITLE_LABEL } from '../services/elusApi';
import './Mairie.css';

// Mapping status backend → classe CSS visuelle de la page Mairie
const STATUS_TO_LAB = {
  en_cours:     { lab: 'En cours',     cls: 'live' },
  phase_finale: { lab: 'Phase finale', cls: 'warn' },
  planifie:     { lab: 'Planifié',     cls: 'plan' },
  livre:        { lab: 'Livré',        cls: 'plan' },
  suspendu:     { lab: 'Suspendu',     cls: 'plan' },
};

// Helpers d'affichage des élus
function adjointSuffix(rank) {
  if (rank === 1) return '1ʳᵉ ';
  if (rank > 1) return `${rank}ᵉ `;
  return '';
}
function formatRole(elu) {
  if (elu.title === 'maire') return elu.delegation || 'Maire de Cambérène';
  const base = elu.title === 'adjoint'
    ? `${adjointSuffix(elu.rank)}adjoint·e`
    : 'Conseiller·ère';
  return elu.delegation ? `${base} · ${elu.delegation}` : base;
}

const SERVICES = [
  { ico: "α", title: "Acte de naissance",       desc: "Copie intégrale ou extrait, délivré sous 48h.",              delay: "48h" },
  { ico: "ω", title: "Acte de mariage",         desc: "Copie ou extrait, retrait au guichet de l'état civil.",      delay: "72h" },
  { ico: "≡", title: "Certificat de résidence", desc: "Attestation officielle de domiciliation à Cambérène.",       delay: "24h" },
  { ico: "✦", title: "Permis de construire",    desc: "Demande, suivi de dossier et validation par la commission.", delay: "30 jours" },
  { ico: "⌖", title: "Autorisation marché",     desc: "Demande d'occupation temporaire pour vente sur la voie publique.", delay: "7 jours" },
  { ico: "◯", title: "Signalement voirie",      desc: "Trou, lampadaire HS, dépôt sauvage — traitement sous 5 jours.",   delay: "5 jours" },
];

const CIVIC = [
  { val: "1.4",  unit: "Md F", lab: "Budget annuel",         sub: "Voté à l'unanimité au conseil du 12 février" },
  { val: "23",   unit: "",      lab: "Chantiers livrés 2025", sub: "Tous secteurs confondus, dans les délais" },
  { val: "4",    unit: "",      lab: "Commissions publiques", sub: "Voirie · Éducation · Environnement · Jeunesse" },
  { val: "92",   unit: "%",    lab: "Transparence budgétaire", sub: "Audit indépendant 2025" },
];

const Contact = () => {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [elus, setElus] = useState([]);
  const [elusLoading, setElusLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.mai-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  // Chantiers : fetch depuis l'API
  useEffect(() => {
    let cancelled = false;
    chantiersApi.list({ limit: 20 })
      .then((data) => { if (!cancelled) { setProjects(data.items); setProjectsLoading(false); } })
      .catch(() => { if (!cancelled) { setProjects([]); setProjectsLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Élus : fetch depuis l'API
  useEffect(() => {
    let cancelled = false;
    elusApi.list({ limit: 50 })
      .then((data) => { if (!cancelled) { setElus(data.items); setElusLoading(false); } })
      .catch(() => { if (!cancelled) { setElus([]); setElusLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Budget total dérivé (extrait du champ texte « 180 M FCFA » → 180)
  const budgetTotal = useMemo(() => {
    return projects.reduce((sum, p) => {
      const m = (p.budget || '').match(/(\d+[.,]?\d*)/);
      return m ? sum + parseFloat(m[1].replace(',', '.')) : sum;
    }, 0);
  }, [projects]);

  // Maire = premier élu avec title 'maire'
  const maire = useMemo(() => elus.find((e) => e.title === 'maire') || null, [elus]);
  // Les autres élus pour la section conseil
  const others = useMemo(() => elus.filter((e) => e.title !== 'maire'), [elus]);

  return (
    <main className="mai-page">
      {/* ============== HERO ============== */}
      <header className="mai-hero">
        <div className="mai-hero-bg" />
        <div className="mai-hero-grain" />
        <div className="mai-hero-seal" aria-hidden="true"><i>CMR</i></div>

        <div className="mai-hero-meta">
          <div><b>2022</b><span>Mandat en cours</span></div>
          <div><b>2027</b><span>Prochaine élection</span></div>
          <div><b>38 400</b><span>Habitants</span></div>
        </div>

        <div className="wrap mai-hero-inner reveal">
          <span className="mai-hero-eyebrow">Municipalité de Cambérène · Mandat 2022 — 2027</span>
          <h1>
            Au service<br />
            <span className="ital">du village.</span>
          </h1>
          <p className="lede">
            La Mairie de Cambérène œuvre chaque jour pour la voirie, l'éducation, la jeunesse,
            l'environnement et la préservation du patrimoine — en transparence et avec la communauté.
          </p>

          <div className="mai-hero-foot">
            <div><b>1.4<span style={{ fontSize: '.5em', color: 'rgba(250,246,238,.6)' }}> Md F</span></b><span>Budget annuel</span></div>
            <div><b>23</b><span>Chantiers livrés 2025</span></div>
            <div><b>{elusLoading ? '—' : elus.length}</b><span>Élus au conseil</span></div>
            <div><b>4</b><span>Commissions publiques</span></div>
          </div>

          <div className="mai-hero-actions">
            <a href="#services" className="btn btn-primary">
              <span>Démarches en ligne</span>
              <span className="arrow">→</span>
            </a>
            <a href="#agenda" className="btn btn-outline" style={{ color: 'var(--bone)', borderColor: 'rgba(250,246,238,.3)' }}>
              <span>Prochain conseil</span>
              <span className="arrow">↗</span>
            </a>
          </div>
        </div>
      </header>

      {/* ============== MOT DU MAIRE ============== */}
      <section className="mai-section mai-mayor">
        <div className="wrap">
          <div className="mai-head reveal">
            <div>
              <span className="eyebrow">Mot du Maire</span>
              <h2>Une mairie<br /><span className="ital">à votre écoute.</span></h2>
            </div>
            <p>
              Élu en 2022, Mamadou Laye conduit un mandat tourné vers l'éducation, l'environnement
              et la jeunesse. Le conseil municipal se réunit tous les premiers vendredis du mois.
            </p>
          </div>

          <div className="mayor-shell">
            <div className="mayor-photo reveal">
              <div
                className="img"
                style={{
                  backgroundImage: `url('${
                    maire?.imageUrl || `https://picsum.photos/seed/${maire?.imageSeed || 'cb-mai-portrait-maire'}/1200/1500`
                  }')`,
                }}
              />
              <div className="grad" />
              <div className="seal">
                CMR<br />
                {maire?.mandateEnd ? new Date(maire.mandateEnd).getFullYear() : '2027'}
              </div>
              <div className="nm">
                <small>
                  Mandat {maire?.electedAt ? new Date(maire.electedAt).getFullYear() : '2022'}
                  {' — '}
                  {maire?.mandateEnd ? new Date(maire.mandateEnd).getFullYear() : '2027'}
                </small>
                <h3>{maire ? maire.fullName : 'Mamadou Laye'}</h3>
              </div>
            </div>

            <div className="mayor-text reveal">
              <span className="quote-tick">"</span>
              <blockquote>
                {maire?.quote ||
                  "Cambérène n'appartient ni au passé ni à demain — elle se construit, chaque matin, par les mains de ses habitants. La Mairie n'est que le prolongement de cette volonté commune."}
              </blockquote>
              <div className="sig">
                <span className="sig-name">
                  {maire ? `${maire.firstName[0]}. ${maire.lastName}` : 'M. Laye'}
                </span>
                <div className="sig-info">
                  <b>{maire ? maire.fullName : 'Mamadou Laye'}</b>
                  <small>
                    Maire de Cambérène
                    {maire?.electedAt && ` · Élu en ${new Date(maire.electedAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`}
                  </small>
                </div>
                <a href="#" className="read-btn">Lire le discours →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== CHANTIERS ============== */}
      <section className="mai-section mai-projects">
        <div className="wrap">
          <div className="mai-head reveal">
            <div>
              <span className="eyebrow">
                Chantiers en cours · {projects.length} projet{projects.length > 1 ? 's' : ''}
              </span>
              <h2>
                {projects.length} projet{projects.length > 1 ? 's' : ''},<br />
                <span className="ital">{budgetTotal > 0 ? `${budgetTotal.toLocaleString('fr-FR')} M FCFA` : 'transparence totale'}.</span>
              </h2>
            </div>
            <p>
              Voirie, école, environnement, jeunesse — l'investissement municipal de l'année,
              suivi en transparence avec la communauté.
            </p>
          </div>

          {projectsLoading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement des chantiers…
            </p>
          )}

          {!projectsLoading && projects.length === 0 && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
              Aucun chantier publié pour l'instant.
            </p>
          )}

          {!projectsLoading && projects.length > 0 && (
            <div className="proj-grid">
              {projects.map((p) => {
                const s = STATUS_TO_LAB[p.status] || { lab: p.status, cls: 'plan' };
                return (
                  <article key={p.id} className="proj-x reveal">
                    <span className="lab">
                      <span className={`dot ${s.cls}`} style={{ position: 'relative' }} />
                      {s.lab} · {CATEGORY_LABEL[p.category] || p.category}
                    </span>
                    <h4>{p.title}</h4>
                    {p.description && <p className="desc">{p.description}</p>}
                    <div className="meta">
                      <b>{p.pct}%</b>
                      <span>{p.delivery || '—'}</span>
                    </div>
                    <div className="bar">
                      <i className={s.cls === 'plan' ? 'plan' : ''} style={{ width: `${p.pct}%` }} />
                    </div>
                    {p.budget && (
                      <div className="budget">
                        Budget alloué · <b>{p.budget}</b>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== CONSEIL MUNICIPAL ============== */}
      <section className="mai-section mai-council">
        <div className="wrap">
          <div className="mai-head reveal">
            <div>
              <span className="eyebrow">Le conseil municipal · {elus.length} élu{elus.length > 1 ? 's' : ''}</span>
              <h2>Maire <span className="ital">& conseil.</span></h2>
            </div>
            <p>
              Maire, adjoints et conseillers municipaux, élus pour cinq ans.
              Tous bénévoles, tous accessibles aux habitants sur rendez-vous.
            </p>
          </div>

          {elusLoading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(250,246,238,.55)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement du conseil…
            </p>
          )}

          {!elusLoading && elus.length === 0 && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(250,246,238,.55)' }}>
              Aucun élu publié pour l'instant.
            </p>
          )}

          {!elusLoading && elus.length > 0 && (
            <div className="cons-grid">
              {elus.map((e, i) => {
                const img = e.imageUrl || `https://picsum.photos/seed/${e.imageSeed || `cb-elu-${e.id}`}/600/600`;
                return (
                  <article key={e.id} className="cons-card reveal">
                    <div className="ph" style={{ backgroundImage: `url('${img}')` }} />
                    <span className="role">{formatRole(e)}</span>
                    <h5>{e.fullName}</h5>
                    <div className="meta">
                      <span>{String(i + 1).padStart(2, '0')}</span>
                      <span>
                        {e.email
                          ? <a href={`mailto:${e.email}`} style={{ color: 'inherit' }}>Contact →</a>
                          : 'Sur RDV →'}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== DÉMARCHES ============== */}
      <section className="mai-section mai-services" id="services">
        <div className="wrap">
          <div className="mai-head reveal">
            <div>
              <span className="eyebrow">Démarches en ligne</span>
              <h2>Six services,<br /><span className="ital">en quelques clics.</span></h2>
            </div>
            <p>
              Les démarches les plus demandées sont désormais accessibles en ligne.
              Pour les autres, l'état civil reçoit du lundi au vendredi, 09h–13h.
            </p>
          </div>

          <div className="serv-grid">
            {SERVICES.map((s) => (
              <article key={s.title} className="serv-card reveal">
                <span className="ico">{s.ico}</span>
                <h5>{s.title}</h5>
                <p>{s.desc}</p>
                <div className="foot">
                  <span className="delay">Délai · {s.delay}</span>
                  <span className="arr">Démarrer →</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CHIFFRES (civic band) ============== */}
      <section className="mai-civic">
        <div className="civic-grid reveal">
          {CIVIC.map((c) => (
            <div key={c.lab} className="civic-cell">
              <span className="lab">{c.lab}</span>
              <b>{c.val}{c.unit && <span className="unit">{c.unit}</span>}</b>
              <span className="sub">{c.sub}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============== AGENDA SÉANCE ============== */}
      <section className="mai-agenda" id="agenda">
        <div className="wrap">
          <div className="mai-head reveal">
            <div>
              <span className="eyebrow" style={{ color: 'rgba(250,246,238,.55)' }}>Prochaine séance publique</span>
              <h2 style={{ color: 'var(--bone)' }}>Le village,<br /><span className="ital" style={{ color: 'var(--gold-soft)' }}>en délibération.</span></h2>
            </div>
            <p style={{ color: 'rgba(250,246,238,.72)' }}>
              Les séances du conseil municipal sont ouvertes au public. L'ordre du jour
              est publié 8 jours avant la séance — tous les habitants peuvent assister.
            </p>
          </div>

          <div className="agenda-card reveal">
            <div className="date">
              <b>17</b>
              <small>Mai · 2026</small>
            </div>
            <div className="info">
              <span className="tag">Conseil municipal · Public</span>
              <h4>Séance ordinaire — Budget participatif 2026</h4>
              <p>
                Salle des délibérations · 16h00 · 5 points à l'ordre du jour, dont le vote
                des trois projets soumis par les habitants pour l'enveloppe participative.
              </p>
            </div>
            <div className="act">
              <a href="#" className="btn-cta">Assister →</a>
              <a href="#" className="btn-cta-ghost">Ordre du jour</a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== CTA / CONTACT ============== */}
      <section className="mai-cta">
        <div className="wrap">
          <div className="mai-head reveal" style={{ marginBottom: 36 }}>
            <div>
              <span className="eyebrow">Contacter la Mairie</span>
              <h2>Quatre portes<br /><span className="ital">d'entrée.</span></h2>
            </div>
            <p>
              Démarches en ligne, signalement d'un problème, délibérations publiques ou
              contact direct avec l'équipe — quel que soit votre besoin, voici où aller.
            </p>
          </div>

          <div className="cta-grid">
            <a href="#" className="cta-tile primary reveal">
              <span className="lab">Service</span>
              <h5>Démarches en ligne</h5>
              <p>État civil, certificats, permis — 6 démarches dématérialisées.</p>
              <span className="arr">Démarrer →</span>
            </a>
            <a href="#" className="cta-tile reveal">
              <span className="lab">Transparence</span>
              <h5>Délibérations & PV</h5>
              <p>Archives publiques des séances du conseil municipal depuis 2022.</p>
              <span className="arr">Consulter →</span>
            </a>
            <Link to="/signaler" className="cta-tile reveal">
              <span className="lab">Signalement</span>
              <h5>Signaler un problème</h5>
              <p>Voirie, propreté, éclairage. Traitement sous 5 jours.</p>
              <span className="arr">Signaler →</span>
            </Link>
            <Link to="/inscription" className="cta-tile reveal">
              <span className="lab">Contact</span>
              <h5>Écrire à l'équipe</h5>
              <p>Question, suggestion, rendez-vous avec un élu — l'équipe vous répond sous 48h.</p>
              <span className="arr">Contacter →</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
